import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CircleDollarSign,
  Languages,
  Loader2,
  Save,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getApiValidationErrors,
  menusApi,
  productsApi,
} from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Supplements/supplement-form.css';
const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const SUPPORTED_LOCALES = [
  'fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr',
];

const createEmptyTranslations = () =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, { name: '', description: '' }]),
  );

const normalizeTranslations = (translations) =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [
      locale,
      {
        name: translations?.[locale]?.name ?? '',
        description: translations?.[locale]?.description ?? '',
      },
    ]),
  );

// When the menu API has no explicit language settings, show all 12.
const getMenuLocales = (menu) => {
  if (!menu) return SUPPORTED_LOCALES;

  const raw = menu.enabled_languages ?? menu.selected_languages ??
    menu.available_languages ?? menu.settings?.enabled_languages ??
    menu.settings?.selected_languages ?? menu.settings?.available_languages;

  if (raw === undefined || raw === null || raw === '') return SUPPORTED_LOCALES;

  let values = raw;
  if (typeof values === 'string') {
    try { values = JSON.parse(values); }
    catch { values = values.split(',').map((entry) => entry.trim()); }
  }
  if (values && !Array.isArray(values) && typeof values === 'object') {
    values = Object.keys(values).filter((key) => Boolean(values[key]));
  }
  if (!Array.isArray(values)) return SUPPORTED_LOCALES;

  const enabled = new Set(values.filter((item) => typeof item === 'string'));
  const defaultLanguage = SUPPORTED_LOCALES.includes(menu.default_language)
    ? menu.default_language : 'fr';
  enabled.add(defaultLanguage);
  return SUPPORTED_LOCALES.filter((locale) => enabled.has(locale));
};

const hasValue = (value) => {
  return value !== null && value !== undefined && String(value).trim() !== '';
};
function SupplementForm() {
  const { supplementId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const isEditMode = Boolean(supplementId);
  const isRtl = language === 'ar';
  const [form, setForm] = useState({
    price: '',
    sort_order: 0,
    is_active: true,
    translations: createEmptyTranslations(),
  });
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localErrors, setLocalErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const [menus, setMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(
    () => localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '',
  );

  useEffect(() => {
    let active = true;

    const loadMenus = async () => {
      try {
        const response = await menusApi.getMenus(establishmentId);
        const data = response.data?.data?.menus ?? response.data?.data ?? [];
        if (!active) return;
        const menuList = Array.isArray(data) ? data : [];
        setMenus(menuList);
        const stored = localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '';
        const selected = menuList.find((menu) => String(menu.id) === String(stored));
        const fallback = selected || menuList.find((menu) => Boolean(menu.is_default)) ||
          menuList.find((menu) => Boolean(menu.is_active)) || menuList[0];
        setSelectedMenuId(fallback?.id ? String(fallback.id) : '');
      } catch {
        if (active) {
          setMenus([]);
          setSelectedMenuId('');
        }
      }
    };

    loadMenus();
    return () => { active = false; };
  }, [establishmentId]);

  const selectedMenu = useMemo(
    () => menus.find((menu) => String(menu.id) === String(selectedMenuId)),
    [menus, selectedMenuId],
  );
  const visibleLocales = useMemo(() => getMenuLocales(selectedMenu), [selectedMenu]);

  const pageTitle = isEditMode
    ? t('supplements.form.editTitle')
    : t('supplements.form.createTitle');
  const pageDescription = isEditMode
    ? t('supplements.form.editDescription')
    : t('supplements.form.createDescription');
  const clearLocalError = (field) => {
    setLocalErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }
      const nextErrors = { ...currentErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  };
  const translateValidationMessage = (message) => {
    if (!message) {
      return '';
    }
    const normalizedMessage = String(message).toLowerCase();
    if (normalizedMessage.includes('required')) {
      return t('validation.messages.required');
    }
    if (normalizedMessage.includes('numeric')) {
      return t('validation.messages.numeric');
    }
    if (normalizedMessage.includes('integer')) {
      return t('validation.messages.integer');
    }
    if (
      normalizedMessage.includes('true or false') ||
      normalizedMessage.includes('boolean')
    ) {
      return t('validation.messages.boolean');
    }
    return message;
  };
  const getFieldError = (field) => {
    if (localErrors[field]) {
      return localErrors[field];
    }
    const message = validationErrors[field]?.[0] || '';
    return translateValidationMessage(message);
  };
  const hasAtLeastOneName = () => {
    return Object.values(form.translations).some((translation) =>
      hasValue(translation.name),
    );
  };
  const validateForm = () => {
    const errors = {};
    if (!hasValue(form.price)) {
      errors.price = t('validation.messages.required');
    }
    if (!hasAtLeastOneName()) {
      errors.translations = t('validation.messages.required');
    }
    return errors;
  };
  const fetchSupplement = async () => {
    if (!isEditMode) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await productsApi.getSupplement(supplementId);
      const supplement = response.data.data;
      setForm({
        price: supplement.price ?? '',
        sort_order: supplement.sort_order || 0,
        is_active: supplement.is_active !== false,
        translations: normalizeTranslations(supplement.translations),
      });
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);
      setError(translatedError);
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSupplement();
  }, [supplementId]);
  const updateField = (field, value) => {
    clearLocalError(field);
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };
  const updateTranslation = (locale, field, value) => {
    clearLocalError('translations');
    clearLocalError(`translations.${locale}.${field}`);
    setForm((currentForm) => ({
      ...currentForm,
      translations: {
        ...currentForm.translations,
        [locale]: {
          ...(currentForm.translations?.[locale] ?? { name: '', description: '' }),
          [field]: value,
        },
      },
    }));
  };
  const buildPayload = () => {
    return {
      price: form.price,
      sort_order: form.sort_order || 0,
      is_active: form.is_active ? 1 : 0,
      translations: form.translations,
    };
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    const frontendErrors = validateForm();
    if (Object.keys(frontendErrors).length > 0) {
      setLocalErrors(frontendErrors);
      setValidationErrors({});
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('validation.messages.required'),
      });
      return;
    }
    try {
      setSaving(true);
      setError('');
      setLocalErrors({});
      setValidationErrors({});
      const payload = buildPayload();
      if (isEditMode) {
        await productsApi.updateSupplement(supplementId, payload);
        showAlert({
          type: 'success',
          title: t('alerts.success'),
          message: t('supplements.alerts.updated'),
        });
      } else {
        await productsApi.createSupplement(establishmentId, payload);
        showAlert({
          type: 'success',
          title: t('alerts.success'),
          message: t('supplements.alerts.created'),
        });
      }
      navigate('/supplements');
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);
      setError(translatedError);
      setValidationErrors(getApiValidationErrors(apiError));
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return (
      <main
        className={`supplement-form-page ${
          isRtl
            ? 'supplement-form-page--rtl'
            : 'supplement-form-page--ltr'
        }`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <section className="supplement-form-loading-panel">
          <div className="supplement-form-loading">
            <Loader2 size={28} />
            <span>{t('supplements.loading')}</span>
          </div>
        </section>
      </main>
    );
  }
  return (
    <main
      className={`supplement-form-page ${
        isRtl
          ? 'supplement-form-page--rtl'
          : 'supplement-form-page--ltr'
      }`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <header className="supplement-form-header">
        <button
          type="button"
          className="supplement-form-back-button"
          onClick={() => navigate('/supplements')}
        >
          {isRtl ? (
            <ArrowRight size={18} />
          ) : (
            <ArrowLeft size={18} />
          )}
          <span>{t('common.back')}</span>
        </button>
        <div className="supplement-form-header__copy">
          <p className="supplement-form-eyebrow">
            {t('supplements.eyebrow')}
          </p>
          <h1>{pageTitle}</h1>
          <span>{pageDescription}</span>
        </div>
      </header>
      {error && (
        <div
          className="supplement-form-alert supplement-form-alert--error"
          role="alert"
        >
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      <form className="supplement-form" onSubmit={handleSubmit} noValidate>
        <section className="supplement-form-section">
          <div className="supplement-form-section__title">
            <span className="supplement-form-section__icon">
              <CircleDollarSign size={20} />
            </span>
            <div>
              <h2>{t('supplements.form.basicInfo')}</h2>
              <p>{t('supplements.form.basicInfoDescription')}</p>
            </div>
          </div>
          <div className="supplement-form__grid">
            <label
              className={`supplement-form__field ${
                getFieldError('price') ? 'error' : ''
              }`}
            >
              <span>{t('supplements.form.price')}</span>
              <input
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) => updateField('price', event.target.value)}
              />
              {getFieldError('price') && (
                <em>{getFieldError('price')}</em>
              )}
            </label>
            <label
              className={`supplement-form__field ${
                getFieldError('sort_order') ? 'error' : ''
              }`}
            >
              <span>{t('supplements.form.sortOrder')}</span>
              <input
                name="sort_order"
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(event) =>
                  updateField('sort_order', event.target.value)
                }
              />
              {getFieldError('sort_order') && (
                <em>{getFieldError('sort_order')}</em>
              )}
            </label>
            <label className="supplement-form__checkbox supplement-form__grid-full">
              <input
                name="is_active"
                type="checkbox"
                checked={form.is_active}
                onChange={(event) =>
                  updateField('is_active', event.target.checked)
                }
              />
              <span>{t('supplements.form.isActive')}</span>
            </label>
          </div>
        </section>
        <section className="supplement-form-section">
          <div className="supplement-form-section__title">
            <span className="supplement-form-section__icon">
              <Languages size={20} />
            </span>
            <div>
              <h2>{t('supplements.form.translations')}</h2>
              <p>{t('supplements.form.translationsDescription')}</p>
            </div>
          </div>
          <div className="supplement-form__translations">
            {visibleLocales.map((locale) => (
              <div className="supplement-form__translation-card" key={locale}>
                <h3>{t(`languages.${locale}`)}</h3>
                <label
                  className={`supplement-form__field ${
                    getFieldError(`translations.${locale}.name`) ? 'error' : ''
                  }`}
                >
                  <span>{t('supplements.form.name')}</span>
                  <input
                    name={`translations.${locale}.name`}
                    type="text"
                    value={form.translations?.[locale]?.name ?? ''}
                    onChange={(event) =>
                      updateTranslation(locale, 'name', event.target.value)
                    }
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />
                  {getFieldError(`translations.${locale}.name`) && (
                    <em>{getFieldError(`translations.${locale}.name`)}</em>
                  )}
                </label>
                <label
                  className={`supplement-form__field ${
                    getFieldError(`translations.${locale}.description`)
                      ? 'error'
                      : ''
                  }`}
                >
                  <span>{t('supplements.form.description')}</span>
                  <textarea
                    name={`translations.${locale}.description`}
                    value={form.translations?.[locale]?.description ?? ''}
                    onChange={(event) =>
                      updateTranslation(
                        locale,
                        'description',
                        event.target.value,
                      )
                    }
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />
                  {getFieldError(`translations.${locale}.description`) && (
                    <em>
                      {getFieldError(`translations.${locale}.description`)}
                    </em>
                  )}
                </label>
              </div>
            ))}
          </div>
          {getFieldError('translations') && (
            <div className="supplement-form-alert supplement-form-alert--error">
              <AlertCircle size={18} />
              <span>{getFieldError('translations')}</span>
            </div>
          )}
        </section>
        <div className="supplement-form__actions">
          <button
            type="button"
            className="supplement-form-cancel-button"
            onClick={() => navigate('/supplements')}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="supplement-form-save-button"
            disabled={saving}
          >
            {saving ? <Loader2 size={18} /> : <Save size={18} />}
            <span>
              {isEditMode
                ? t('supplements.form.update')
                : t('supplements.form.save')}
            </span>
          </button>
        </div>
      </form>
    </main>
  );
}
export default SupplementForm;
